import { Component, inject, AfterViewInit, DestroyRef, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AlertComponent, ButtonComponent, InputComponent } from '@verticalbridge/ui-components';
import { PortalAuthService } from '../../core/auth/portal-auth.service';
import { RequestAccessService } from '../../core/services/request-access.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { AuthLayoutComponent } from '../../shared/components/auth-layout/auth-layout.component';

function passwordsMatchValidator(control: AbstractControl): ValidationErrors | null {
  const password = control.get('password');
  const confirmPassword = control.get('confirmPassword');
  if (!password || !confirmPassword) return null;
  return password.value === confirmPassword.value ? null : { passwordsMismatch: true };
}

function notVerticalBridgeEmailValidator(control: AbstractControl): ValidationErrors | null {
  const email = control.value as string;
  if (!email) return null;
  return email.toLowerCase().endsWith('@verticalbridge.com') ? { verticalBridgeEmail: true } : null;
}

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    AuthLayoutComponent,
    AlertComponent,
    ButtonComponent,
    InputComponent,
  ],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.scss'
})
export class SignupComponent implements AfterViewInit {
  private authService = inject(PortalAuthService);
  private requestAccessService = inject(RequestAccessService);
  protected router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private el = inject(ElementRef);
  private fb = inject(FormBuilder);

  form = this.fb.group({
    firstName: ['', [Validators.required, Validators.pattern(/\S/)]],
    lastName: ['', [Validators.required, Validators.pattern(/\S/)]],
    email: ['', [Validators.required, Validators.email, notVerticalBridgeEmailValidator]],
    password: ['', [Validators.required, Validators.minLength(8)]],
    confirmPassword: ['', Validators.required]
  }, { validators: passwordsMatchValidator });

  isLoading = false;
  errorMessage = '';
  currentYear = new Date().getFullYear();

  ngAfterViewInit() {
    queueMicrotask(() => {
      const firstField = this.el.nativeElement.querySelector('input') as HTMLInputElement | null;
      firstField?.focus();
    });
  }

  signUp() {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      if (this.form.controls.email.hasError('verticalBridgeEmail')) {
        this.errorMessage = 'Vertical Bridge employees must use Microsoft to log in.';
      } else if (this.form.controls.email.hasError('email')) {
        this.errorMessage = 'Please enter a valid email address';
      } else if (this.form.hasError('passwordsMismatch')) {
        this.errorMessage = 'Passwords do not match';
      } else if (this.form.controls.password.hasError('minlength')) {
        this.errorMessage = 'Password must be at least 8 characters long';
      } else {
        this.errorMessage = 'Please fill in all required fields';
      }
      this.scrollFormToTop();
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.form.disable();

    const { firstName, lastName, email, password } = this.form.getRawValue();

    // Clear any existing auth session before signup
    this.authService.clearAuthState();

    const trimmedFirstName = firstName?.trim() || undefined;
    const trimmedLastName = lastName?.trim() || undefined;
    const fullName = [trimmedFirstName, trimmedLastName].filter(Boolean).join(' ') || undefined;

    this.authService.signUpDirect(email!, password!, { name: fullName, givenName: trimmedFirstName, familyName: trimmedLastName }).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        // Auto-login, then provision the Colo user record (handled in
        // RequestAccessService.ensureColoUserProvisioned: GetUserData →
        // CreateUser if null → refreshToken).
        this.authService.loginDirect(email!, password!).pipe(
          switchMap(() =>
            this.requestAccessService.ensureColoUserProvisioned(
              email!,
              firstName ?? undefined,
              lastName ?? undefined
            )
          ),
          // Notify the Site Locator recipient list that a new user signed up
          // and gained Online Colo access. Best-effort: a failure here must
          // not block signup completion.
          switchMap(() =>
            this.requestAccessService.notifyPortalSignup(email!, fullName).pipe(
              catchError(() => of(null))
            )
          ),
          takeUntilDestroyed(this.destroyRef)
        ).subscribe({
          next: () => {
            this.isLoading = false;
            this.form.enable();
            if (!this.authService.handlePostLoginRedirect()) {
              this.router.navigate(['/home']);
            }
          },
          error: (err) => {
            // The account was created, but the post-signup auto-login (or Colo
            // provisioning) failed. Surface the real reason instead of showing
            // a generic "check your email" message and pretending it worked.
            console.error('[Signup] account created but auto-login/provisioning failed:', err);
            this.isLoading = false;
            this.form.enable();
            this.errorMessage =
              err?.error?.error_description ||
              err?.error?.description ||
              err?.error?.message ||
              err?.message ||
              'Your account was created, but automatic sign-in failed. Please try logging in.';
          }
        });
      },
      error: (error) => {
        this.isLoading = false;
        this.form.enable();

        if (error.error?.code === 'invalid_signup') {
          this.errorMessage = 'Unable to create account. This email may already be registered.';
        } else if (error.error?.description) {
          this.errorMessage = error.error.description;
        } else if (error.error?.message) {
          this.errorMessage = error.error.message;
        } else if (error.status === 400) {
          this.errorMessage = 'Invalid signup details. Please check your information and try again.';
        } else {
          this.errorMessage = 'Sign up failed. Please try again.';
        }
        this.scrollFormToTop();
      }
    });
  }

  loginWithSocial(provider: string) {
    this.authService.loginWithProvider(provider);
  }

  private scrollFormToTop(): void {
    const formPanel = this.el.nativeElement.closest('.auth-form-panel');
    if (formPanel) {
      formPanel.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
}
